'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Clock, ChevronLeft, ChevronRight, BookOpen, ArrowRight, User } from 'lucide-react';
import type { BlogPostListItem, BlogCategory } from '@/lib/api';
import { blogApi } from '@/lib/api';
import { isKnownImageHost } from '@/lib/image-hosts';
import { useTranslations } from 'next-intl';
import { PAGE_CONTAINER } from '@/components/ui';

const TEAL_600 = '#0d9488';
// Light enough to read on the teal-700 featured card (teal-300 is only ~3.7:1 there).
const TEAL_100 = '#ccfbf1';

interface BlogListClientProps {
  initialPosts: BlogPostListItem[];
  initialTotal: number;
  initialTotalPages: number;
  featuredPosts: BlogPostListItem[];
  categories: BlogCategory[];
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '';
  // timeZone is pinned so the SSR pass (container TZ, usually UTC) and the
  // hydration pass (browser TZ) format the same day — otherwise timestamps near
  // local midnight render differently and trip a hydration mismatch.
  return new Date(dateStr).toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Jakarta',
  });
}

function PostCard({ post }: { post: BlogPostListItem }) {
  const t = useTranslations('blog');
  const categoryColor = post.category?.color || TEAL_600;
  const coverUrl = post.coverImage?.url || post.ogImageUrl || `/blog/${post.slug}/opengraph-image`;
  return (
    <Link href={`/blog/${post.slug}`} className="group block h-full">
      <article className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md hover:border-teal-200 transition-all duration-200 h-full flex flex-col">
        {coverUrl ? (
          <div className="relative aspect-[16/9] overflow-hidden">
            {/* Grid card below the featured section on first paint — never the LCP
                candidate, so default (lazy) next/image behavior is correct here. */}
            <Image
              src={coverUrl}
              alt={post.title}
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              unoptimized={!isKnownImageHost(coverUrl)}
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ) : (
          // No cover image in the data — purely typographic card, just a slim category accent bar instead of an empty image well
          <div className="h-1" style={{ backgroundColor: categoryColor }} />
        )}
        <div className="p-5 flex-1 flex flex-col">
          {post.category && (
            <span
              className="inline-block text-[11px] font-semibold uppercase tracking-wide mb-2 w-fit"
              style={{ color: categoryColor }}
            >
              {post.category.name}
            </span>
          )}
          <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-teal-700 transition-colors line-clamp-2 leading-snug">
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="text-sm text-slate-500 mb-4 line-clamp-3 flex-1 leading-relaxed">{post.excerpt}</p>
          )}

          {/* Author + meta */}
          <div className="flex items-center gap-2 pt-3 border-t border-slate-100 mt-auto">
            <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
              <User className="w-3.5 h-3.5 text-teal-600" />
            </div>
            <span className="text-sm text-slate-600 font-medium truncate min-w-0">
              {post.author?.displayName || 'Tim Aksademia'}
            </span>
            <span className="text-slate-300 text-sm">·</span>
            <span className="text-sm text-slate-500 whitespace-nowrap">{formatDate(post.publishedAt)}</span>
            <span className="flex items-center gap-0.5 ml-auto text-sm text-slate-500 shrink-0">
              <Clock className="w-3 h-3" />
              {t('minRead', { mins: post.readingTimeMins })}
            </span>
          </div>

          <span className="inline-flex items-center gap-1 text-xs font-semibold text-teal-600 group-hover:text-teal-700 mt-3">
            {t('bacaSelengkapnya')} <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </article>
    </Link>
  );
}

// Primary featured article — text-forward lead on a solid dark surface, filled with real content
function PrimaryFeaturedCard({ post, spanFull }: { post: BlogPostListItem; spanFull: boolean }) {
  const t = useTranslations('blog');
  return (
    <Link href={`/blog/${post.slug}`} className={`group block h-full ${spanFull ? 'lg:col-span-3' : 'lg:col-span-2'}`}>
      <article className="bg-pattern overflow-hidden h-full bg-teal-700 rounded-2xl p-6 sm:p-8 lg:p-10 flex flex-col justify-between hover:shadow-md transition-shadow duration-200">
        <div>
          {post.category && (
            <span className="inline-block text-xs font-semibold uppercase tracking-wide mb-4" style={{ color: TEAL_100 }}>
              {post.category.name}
            </span>
          )}
          <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4 group-hover:text-teal-200 transition-colors line-clamp-3">
            {post.title}
          </h3>
          {post.excerpt && (
            <p className="text-sm sm:text-base text-teal-50 leading-relaxed line-clamp-3 max-w-2xl">
              {post.excerpt}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-teal-100 mt-6 pt-6 border-t border-teal-600">
          <span className="font-medium text-white truncate min-w-0">{post.author?.displayName || 'Tim Aksademia'}</span>
          <span>·</span>
          <span className="whitespace-nowrap">{formatDate(post.publishedAt)}</span>
          <span className="flex items-center gap-1 ml-auto text-white font-semibold shrink-0">
            {t('bacaSelengkapnya')} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </article>
    </Link>
  );
}

// Secondary featured article — compact typographic card, sits beside the primary lead
function SecondaryFeaturedCard({ post }: { post: BlogPostListItem }) {
  const t = useTranslations('blog');
  const categoryColor = post.category?.color || TEAL_600;
  return (
    <Link href={`/blog/${post.slug}`} className="group block">
      <article className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-teal-200 transition-all duration-200">
        {post.category && (
          <span className="inline-block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: categoryColor }}>
            {post.category.name}
          </span>
        )}
        <h4 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2 mb-2 group-hover:text-teal-700 transition-colors">
          {post.title}
        </h4>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="whitespace-nowrap">{formatDate(post.publishedAt)}</span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('minRead', { mins: post.readingTimeMins })}
          </span>
        </div>
      </article>
    </Link>
  );
}

function FeaturedSection({ posts }: { posts: BlogPostListItem[] }) {
  if (posts.length === 0) return null;

  const [primary, ...rest] = posts;
  const secondary = rest.slice(0, 3);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <PrimaryFeaturedCard post={primary} spanFull={secondary.length === 0} />
      {secondary.length > 0 && (
        <div className="flex flex-col gap-4">
          {secondary.map(post => <SecondaryFeaturedCard key={post.id} post={post} />)}
        </div>
      )}
    </div>
  );
}

function PostCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
      <div className="aspect-[16/9] bg-slate-200" />
      <div className="p-5 space-y-3">
        <div className="h-3 bg-slate-200 rounded w-1/4" />
        <div className="h-5 bg-slate-200 rounded w-3/4" />
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-4/5" />
        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
          <div className="w-6 h-6 bg-slate-200 rounded-full" />
          <div className="h-3 bg-slate-200 rounded w-24" />
          <div className="h-3 bg-slate-200 rounded w-16 ml-auto" />
        </div>
      </div>
    </div>
  );
}

function EmptyState({ hasFilter, onClear }: { hasFilter: boolean; onClear: () => void }) {
  const t = useTranslations('blog');
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
        <BookOpen className="w-8 h-8 text-slate-300" />
      </div>
      <h3 className="font-semibold text-slate-700 mb-1">
        {hasFilter ? t('tidakAdaArtikelKategori') : t('belumAdaArtikel')}
      </h3>
      <p className="text-slate-400 text-sm max-w-xs mt-1">
        {hasFilter ? t('cobaKategoriLain') : t('artikelBaruSegera')}
      </p>
      {hasFilter && (
        <button
          onClick={onClear}
          className="mt-4 px-4 py-2 text-sm font-medium text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
        >
          {t('lihatSemuaArtikel')}
        </button>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  const t = useTranslations('blog');
  const pages = buildPageRange(page, totalPages);
  return (
    <div className="flex items-center justify-center gap-1 mt-10">
      <button
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
        <span className="hidden sm:inline">{t('sebelumnya')}</span>
      </button>
      <div className="flex items-center gap-1">
        {pages.map((p, i) =>
          p === '...' ? (
            <span key={`ellipsis-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p as number)}
              className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-teal-600 text-white border border-teal-600'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              {p}
            </button>
          )
        )}
      </div>
      <button
        onClick={() => onChange(page + 1)}
        disabled={page === totalPages}
        className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <span className="hidden sm:inline">{t('berikutnya')}</span>
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function buildPageRange(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

export function BlogListClient({
  initialPosts,
  initialTotal,
  initialTotalPages,
  featuredPosts,
  categories,
}: BlogListClientProps) {
  const t = useTranslations('blog');
  const [posts, setPosts] = useState(initialPosts);
  const [total, setTotal] = useState(initialTotal);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPosts = async (newPage: number, categorySlug: string | null) => {
    setIsLoading(true);
    try {
      const result = await blogApi.getPosts({
        page: String(newPage),
        category: categorySlug || undefined,
      });
      setPosts(result.data);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      setPage(newPage);
    } catch {
      // Keep current state on error
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryFilter = (slug: string | null) => {
    setActiveCategory(slug);
    fetchPosts(1, slug);
  };

  const handlePageChange = (newPage: number) => {
    fetchPosts(newPage, activeCategory);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const activeCategoryName = categories.find(c => c.slug === activeCategory)?.name;
  const pageSize = 12;

  return (
    <>
      {/* Page header — consistent with other catalog pages */}
      <div className="bg-pattern bg-teal-700 py-8 sm:py-10 text-white">
        <div className={PAGE_CONTAINER}>
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight mb-1">{t('title')}</h1>
          <p className="text-teal-50 text-sm">
            {isLoading ? t('loading') : t('artikelTersedia', { total })}
            {activeCategoryName && (
              <span className="font-semibold text-white"> · {activeCategoryName}</span>
            )}
          </p>
        </div>
      </div>

      <div className={`${PAGE_CONTAINER} py-8 pb-16`}>
        {/* Featured posts — only shown when not filtering by category */}
        {featuredPosts.length > 0 && !activeCategory && (
          <section className="mb-10">
            <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-4">
              {t('artikelUnggulan')}
            </h2>
            <FeaturedSection posts={featuredPosts} />
          </section>
        )}

        {/* Category filter tabs */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => handleCategoryFilter(null)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeCategory === null
                  ? 'bg-teal-600 text-white'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700'
              }`}
            >
              {t('semua')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryFilter(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.slug
                    ? 'bg-teal-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:text-teal-700'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        )}

        {/* Result count */}
        {!isLoading && posts.length > 0 && (
          <p className="text-sm text-slate-500 mb-5">
            {t('menampilkan', {
              from: (page - 1) * pageSize + 1,
              to: Math.min(page * pageSize, total),
              total,
            })}
          </p>
        )}

        {/* Post grid / skeletons / empty */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <PostCardSkeleton key={i} />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <EmptyState
            hasFilter={!!activeCategory}
            onClear={() => handleCategoryFilter(null)}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={handlePageChange} />
            )}
          </>
        )}
      </div>
    </>
  );
}
